import { customElement, property, query } from 'lit/decorators.js';
import { TmElement } from '@thiememeulenhoff/lit-sdk';
import { html, PropertyValues } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { clamp } from 'lodash-es';
import { tagName } from '../../helpers/element.helper';
import { styles } from './progress-bar.styles';

const tag = tagName('progress-bar');

@customElement(tag)
export class ProgressBarComponent extends TmElement {
  static override styles = styles;

  @property({ type: Number, reflect: true })
  min = 0;

  @property({ type: Number, reflect: true })
  max = 100;

  @property({ type: Number, reflect: true })
  value = 0;

  @property({ type: String, reflect: true })
  orientation: 'horizontal' | 'vertical' = 'vertical';

  @property({ type: Number, reflect: true })
  thickness = 10;

  @query('.progress')
  readonly progressElement!: HTMLElement;

  #progress?: number;

  protected override shouldUpdate(changedProperties: PropertyValues): boolean {
    if (
      this.#progress !== undefined &&
      changedProperties.size === 1 &&
      changedProperties.has('value')
    ) {
      this.#updateProgressManually();
      return false;
    }

    return super.shouldUpdate(changedProperties);
  }

  protected override willUpdate(changedProperties: PropertyValues<this>) {
    if (
      changedProperties.has('value') ||
      changedProperties.has('max') ||
      changedProperties.has('min')
    ) {
      this.#calculateProgress();
    }
  }

  #updateProgressManually() {
    this.#calculateProgress();

    if (this.orientation === 'horizontal') {
      this.progressElement.style.width = `${this.#progress}%`;
    } else {
      this.progressElement.style.height = `${this.#progress}%`;
    }
  }

  #calculateProgress() {
    const delta = this.max - this.min;

    if (delta) {
      // prevent devide by 0 error
      this.#progress = clamp((this.value - this.min) / delta, 0, 1) * 100;
    }
  }

  override render() {
    return html`
      <div
        class="bar"
        style=${styleMap(
          this.orientation === 'horizontal'
            ? { height: `${this.thickness}px` }
            : { width: `${this.thickness}px` },
        )}
      >
        <div class="progress"></div>
      </div>
    `;
  }

  protected override updated(changedProperties: PropertyValues) {
    if (changedProperties.has('value')) {
      this.#updateProgressManually();
    }

    super.updated(changedProperties);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'a-progress-bar': ProgressBarComponent;
  }
}
